import { FC, useEffect, useMemo } from 'react';
import moment from 'moment';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material';
// components
import { CloseIcon } from 'assets/svgs';
import { Alert } from 'components/common/Alert';
import { LoadingButton } from 'components/common/LoadingButton';
import StaffCard from 'components/main/Staff/Add/StaffCard';
import DatePickerController from 'controller/DatePickerController';
import SelectController from 'controller/Select';
// Schema, graphql, constants, interfaces
import {
  ADD_STAFF_TEXT,
  ADD_TEXT,
  HTTP_STATUS,
  PLAN_TEXT,
  STAFF_IS_CREATING_TEXT,
  TRIAL_END_DATE_TEXT,
  TRIAL_MAX_DAYS,
  addOrgStaffInitialValue,
} from 'constants/index';
import { addOrgStaffSchema } from 'validationSchema';
import { useCreateOrgStaffMutation, useFindPriceAndProductsLazyQuery } from 'generated/graphql';
import { AddOrgStaffDialogProps, AddOrgStaffFormType, SelectType } from 'interfaces';

const AddOrgStaffDialog: FC<AddOrgStaffDialogProps> = ({ open, onClose, fetch, organizationId }) => {
  const methods = useForm<AddOrgStaffFormType>({
    defaultValues: addOrgStaffInitialValue,
    resolver: yupResolver(addOrgStaffSchema),
  });

  const { handleSubmit, reset } = methods;

  const [getPriceAndProducts, { data: pricesData, loading: pricesLoading }] =
    useFindPriceAndProductsLazyQuery({
      onError: ({ message }) => {
        Alert.error(message);
      },
    });

  const { prices } = pricesData?.findPriceAndProducts || {};

  const planOptions: SelectType[] = useMemo(
    () =>
      prices?.map((price) => {
        const { id, product, interval, amount } = price || {};
        const { name } = product || {};
        return {
          value: id ?? '',
          name: `${name ?? ''} - $${amount ?? ''} / ${interval?.toLowerCase() ?? ''}`,
        };
      }) ?? [],
    [prices],
  );

  const handleClose = () => {
    reset(addOrgStaffInitialValue);
    onClose();
  };

  const [createOrgStaff, { loading }] = useCreateOrgStaffMutation({
    onCompleted: (data) => {
      const { createOrgStaff } = data;
      const { response } = createOrgStaff || {};
      const { status, message } = response || {};

      if (status === HTTP_STATUS.CREATED || status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        fetch();
        handleClose();
      } else {
        Alert.warning(message || '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  useEffect(() => {
    if (open) {
      getPriceAndProducts();
    }
  }, [getPriceAndProducts, open]);

  const onSubmit: SubmitHandler<AddOrgStaffFormType> = async (data) => {
    const { firstName, lastName, email, phone, password, role, priceId, trialEnd } = data || {};

    await createOrgStaff({
      variables: {
        createOrgStaffInput: {
          firstName,
          lastName,
          password,
          priceId,
          organizationId,
          staffRole: role ?? '',
          email: email?.toLowerCase() ?? '',
          ...(phone && { phone }),
          trialEnd: moment(trialEnd).endOf('day').toISOString(),
        },
      },
    });
  };

  const minDate = moment().add(1, 'day').startOf('day').toISOString();
  const maxDate = moment().add(TRIAL_MAX_DAYS, 'days').endOf('day').toISOString();

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle variant="h6">{ADD_STAFF_TEXT}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        disabled={loading}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}>
        <CloseIcon />
      </IconButton>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <StaffCard loading={false} />
              </Grid>

              <Grid item xs={12} md={6}>
                <SelectController
                  isRequired
                  name="priceId"
                  title={PLAN_TEXT}
                  options={planOptions}
                  disabled={loading || pricesLoading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePickerController
                  isRequired
                  name="trialEnd"
                  minDate={minDate}
                  maxDate={maxDate}
                  disabled={loading}
                  title={TRIAL_END_DATE_TEXT}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <LoadingButton
              type="submit"
              size="small"
              text={ADD_TEXT}
              loading={loading}
              loadingText={STAFF_IS_CREATING_TEXT}
            />
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddOrgStaffDialog;
